import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Inline } from "components/layout/reusableLayouts";

export interface ComplianceNoticeProps {
  /**
   * Whose limits are being described. `"program"` covers a connection and every
   * card on it; `"card"` narrows the wording to the one card in view.
   */
  subject?: "program" | "card";
}

/**
 * What Outpave can and cannot do with an Astrada-connected program.
 *
 * Required by OUT-1054, and shown per program rather than once per page: it lives
 * in the two drawers, not on the connection card. See README.md.
 */
const ComplianceNotice: React.FC<ComplianceNoticeProps> = ({
  subject = "program",
}) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        borderRadius: "var(--radius-lg)",
        padding: "var(--space-4)",
        backgroundColor: "var(--color-bg-info)",
      }}
    >
      <Inline gap="var(--space-3)" align="start" wrap={false}>
        <HugeiconsIcon
          icon={InformationCircleIcon}
          size={18}
          color="var(--color-text-tertiary)"
          // The icon is decorative: the text beside it carries the whole message.
          style={{ flexShrink: 0, marginTop: "2px" }}
        />
        <Typography
          variant="body2"
          sx={{ minWidth: 0, color: "var(--color-text-tertiary)" }}
        >
          {t(
            subject === "card"
              ? "AstradaCardComplianceNotice"
              : "AstradaComplianceNotice",
          )}
        </Typography>
      </Inline>
    </Box>
  );
};

export default ComplianceNotice;
